/**
 * useAppSettings Hook
 * Manages user preferences including wallpaper, blur, overlay opacity, and sound.
 */
import { useState, useEffect, useCallback } from 'react';

export const WALLPAPER_PRESETS = [
  {
    id: 'default',
    name: 'Mặc định',
    thumbnail: 'linear-gradient(135deg, #1e293b, #0f172a)',
    url: '',
  },
  {
    id: 'sunset',
    name: 'Hoàng hôn',
    thumbnail: 'linear-gradient(135deg, #f97316, #ec4899, #8b5cf6)',
    url: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=1920&q=80',
  },
  {
    id: 'forest',
    name: 'Rừng sương mù',
    thumbnail: 'linear-gradient(135deg, #064e3b, #059669, #34d399)',
    url: 'https://images.unsplash.com/photo-1448375240586-882707db888b?auto=format&fit=crop&w=1920&q=80',
  },
  {
    id: 'lofi',
    name: 'Góc học tập',
    thumbnail: 'linear-gradient(135deg, #78350f, #b45309, #d97706)',
    url: 'https://images.unsplash.com/photo-1518455027359-f3f8164ba6bd?auto=format&fit=crop&w=1920&q=80',
  },
  {
    id: 'cyberpunk',
    name: 'Cyberpunk',
    thumbnail: 'linear-gradient(135deg, #09090b, #4c1d95, #06b6d4)',
    url: 'https://images.unsplash.com/photo-1509198397868-475647b2a1e5?auto=format&fit=crop&w=1920&q=80',
  },
  {
    id: 'mountains',
    name: 'Đỉnh núi',
    thumbnail: 'linear-gradient(135deg, #1e293b, #475569, #94a3b8)',
    url: 'https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?auto=format&fit=crop&w=1920&q=80',
  },
  {
    id: 'zen',
    name: 'Tối giản',
    thumbnail: 'linear-gradient(135deg, #27272a, #3f3f46, #71717a)',
    url: 'https://images.unsplash.com/photo-1518655048521-f130df041f66?auto=format&fit=crop&w=1920&q=80',
  },
];

export const DEFAULT_SETTINGS = {
  wallpaperId: 'default',
  customWallpaperUrl: '',
  backgroundBlur: 2,
  backgroundOverlayOpacity: 0.35,
  soundEffects: false,
};

export function getActiveWallpaperUrl(settings) {
  if (settings.wallpaperId === 'custom' && settings.customWallpaperUrl.trim()) {
    return settings.customWallpaperUrl.trim();
  }
  const preset = WALLPAPER_PRESETS.find((p) => p.id === settings.wallpaperId);
  return preset ? preset.url : '';
}

export function useAppSettings() {
  const [settings, setSettings] = useState(() => {
    try {
      const saved = localStorage.getItem('tm-matrix-settings');
      return saved ? { ...DEFAULT_SETTINGS, ...JSON.parse(saved) } : DEFAULT_SETTINGS;
    } catch {
      return DEFAULT_SETTINGS;
    }
  });

  useEffect(() => {
    localStorage.setItem('tm-matrix-settings', JSON.stringify(settings));
  }, [settings]);

  const updateSetting = useCallback((key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  }, []);

  const selectWallpaper = useCallback((id) => {
    setSettings((prev) => ({ ...prev, wallpaperId: id }));
  }, []);

  const setCustomWallpaper = useCallback((url) => {
    setSettings((prev) => ({
      ...prev,
      wallpaperId: 'custom',
      customWallpaperUrl: url,
    }));
  }, []);

  const resetSettings = useCallback(() => {
    setSettings(DEFAULT_SETTINGS);
  }, []);

  const activeWallpaperUrl = getActiveWallpaperUrl(settings);

  return {
    settings,
    activeWallpaperUrl,
    updateSetting,
    selectWallpaper,
    setCustomWallpaper,
    resetSettings,
  };
}
